import { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../../context/analytics';
import { useWelcomeContext } from '../WelcomeContext';

const RECEIVED_GIFT_QUERY = gql`
  query ReceivedGift {
    receivedGift {
      termMonths
      gifter {
        name
        slug
      }
    }
  }
`;

/** Once per browser session: back-navigation remounts the banner, and a per-mount ref would count it again. */
const SHOWN_KEY = 'ah.welcomeGiftShown';

/** Fallback when sessionStorage is unavailable (private mode): once per page load instead. */
let shownThisLoad = false;

/** True the first time this session. */
function firstShow(): boolean {
  try {
    if (window.sessionStorage.getItem(SHOWN_KEY)) return false;
    window.sessionStorage.setItem(SHOWN_KEY, '1');
    return true;
  } catch {
    if (shownThisLoad) return false;
    shownThisLoad = true;
    return true;
  }
}

interface ReceivedGiftData {
  receivedGift: { termMonths: number; gifter: { name: string | null; slug: string | null } | null } | null;
}

/**
 * Tells a gift-invite recipient the gift is on their account (provisioning or active). Without it the wizard never
 * mentions the subscription she signed up for, and the first trace of it is a bell row.
 * Renders nothing when there is no gift, while loading, or in the admin preview.
 */
export default function GiftBanner() {
  const { t } = useTranslation('welcome-onboarding');
  const { isPreview } = useWelcomeContext();
  const { amplitude } = useAnalytics();
  const { data } = useQuery<ReceivedGiftData>(RECEIVED_GIFT_QUERY, { skip: isPreview });
  const gift = data?.receivedGift;

  useEffect(() => {
    if (!gift || !firstShow()) return;
    amplitude.track('sn_welcome_gift_shown', { termMonths: gift.termMonths });
  }, [gift, amplitude]);

  if (!gift) return null;

  const name = gift.gifter?.name;
  const message = name ? t('gift.from', { name, count: gift.termMonths }) : t('gift.anon', { count: gift.termMonths });

  return (
    <div
      role="status"
      data-testid="welcome-gift-banner"
      className="mx-auto mb-4 flex w-full max-w-[620px] items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-left"
    >
      <span className="text-xl leading-none" aria-hidden>
        🎁
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-wide text-green-800 uppercase">{t('gift.eyebrow')}</p>
        <p className="mt-1 text-sm text-gray-900">{message}</p>
        <p className="mt-0.5 text-sm text-gray-600">{t('gift.added')}</p>
      </div>
    </div>
  );
}
